/**
 * F-170 (B4.7 / B0.15) — Inline review form rendered inside a chat bubble.
 *
 * Backend emits a `kind='order_review_request'` message in the per-order
 * chat when the specialist completes the order. `MessageBubble` swaps
 * the default text rendering for this component so the client can rate
 * + comment without leaving the conversation (or dispute the close).
 */
import { useState } from 'react';
import { Button, Icon, Textarea } from '@/components/atoms';
import { toast } from '@/components/atoms/Toast';
import { reviews as reviewsApi } from '@/api';
import { logger } from '@/utils/logger';
import styles from './InlineReviewForm.module.css';

export interface InlineReviewFormProps {
  orderId: number;
  onSubmitted: () => void;
  onDisputed: () => void;
}

const MAX_BODY = 500;

export function InlineReviewForm({
  orderId,
  onSubmitted,
  onDisputed,
}: InlineReviewFormProps): JSX.Element {
  const [rating, setRating] = useState(0);
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (): Promise<void> => {
    if (rating < 1) {
      toast.error('Постав оцінку від 1 до 5 зірок');
      return;
    }
    setBusy(true);
    try {
      await reviewsApi.submitInlineReview({
        order_id: orderId,
        rating,
        body: body.trim() || undefined,
      });
      toast.success('Дякую за відгук');
      onSubmitted();
    } catch (err) {
      logger.warn('submitInlineReview failed', err);
      toast.error('Не вдалось надіслати відгук');
    } finally {
      setBusy(false);
    }
  };

  const dispute = async (): Promise<void> => {
    setBusy(true);
    try {
      await reviewsApi.disputeCompletion(orderId);
      toast.info('Спір відкрито — майстер отримає сповіщення');
      onDisputed();
    } catch (err) {
      logger.warn('disputeCompletion failed', err);
      toast.error('Не вдалось відкрити спір');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={styles.root}>
      <p className={styles.prompt}>
        Як пройшло? Заповни короткий відгук — допоможе іншим клієнтам.
      </p>
      <div className={styles.stars} role="radiogroup" aria-label="Оцінка">
        {[1, 2, 3, 4, 5].map((n) => (
          <button
            key={n}
            type="button"
            role="radio"
            aria-checked={rating === n}
            aria-label={`${n} з 5`}
            className={styles.star}
            data-active={n <= rating ? 'true' : undefined}
            onClick={() => setRating(n)}
            disabled={busy}
          >
            <Icon name="star" size={26} fill={n <= rating ? 'currentColor' : 'none'} />
          </button>
        ))}
      </div>
      <Textarea
        value={body}
        onChange={(e) => setBody(e.target.value.slice(0, MAX_BODY))}
        rows={3}
        placeholder="Коротко: що сподобалось, що покращити?"
        aria-label="Текст відгуку"
      />
      <div className={styles.bodyMeta}>{body.length}/{MAX_BODY}</div>
      <div className={styles.actions}>
        <Button onClick={() => void submit()} disabled={busy || rating < 1}>
          {busy ? 'Надсилаю…' : 'Залишити відгук'}
        </Button>
        <button
          type="button"
          className={styles.dispute}
          onClick={() => void dispute()}
          disabled={busy}
        >
          Послуги не було… певно закрито помилково
        </button>
      </div>
    </div>
  );
}
